import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

import {
  ArrowRight,
  CheckCircle,
  Cpu,
  GearSix,
  Lightning,
  Lightbulb,
  Phone,
  CalendarCheck,
  PaperPlaneTilt,
} from '@phosphor-icons/react';

const IMAGES = {
  hero: 'https://customer-assets.emergentagent.com/job_eee2203e-55fa-4782-b8f0-fcb6857cc0a0/artifacts/21ez3p8o_Service-img1.png',
  team: 'https://customer-assets.emergentagent.com/job_eee2203e-55fa-4782-b8f0-fcb6857cc0a0/artifacts/ajlzt2d4_Service-team1.png',
  techDetail: 'https://customer-assets.emergentagent.com/job_eee2203e-55fa-4782-b8f0-fcb6857cc0a0/artifacts/qtfxzutp_Service-img2.png',
};

const PANEL = 'rounded-2xl bg-[#111820] border border-[rgba(255,255,255,0.08)] shadow-[0_8px_40px_rgba(0,0,0,0.5)]';

const NewHome = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Man & Machine. Run Your Service Business Automatically",
    "description": "AI systems that handle leads, scheduling, follow-ups, and admin for service businesses. Plumbing, electrical, HVAC, cleaning, and more.",
    "url": "https://manandmachine.app",
    "mainEntity": {
      "@type": "SoftwareApplication",
      "name": "Man & Machine",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": { "@type": "AggregateOffer", "priceCurrency": "USD", "lowPrice": "0", "highPrice": "199" },
      "featureList": ["Lead Capture", "Job Scheduling", "Automated Follow-ups", "Admin Automation", "Revenue Recovery"]
    }
  };

  return (
    <div className="min-h-screen bg-[#161B26] text-white overflow-x-hidden">
      <SEO
        title="Man & Machine. Run Your Service Business Automatically"
        description="AI systems that handle leads, scheduling, follow-ups, and admin for service businesses. Set up in minutes. Results from day one."
        keywords="service business automation, AI for plumbers, AI for electricians, HVAC automation, trade business software, automated lead capture, job scheduling software"
        canonicalUrl="https://manandmachine.app"
        structuredData={structuredData}
      />
      <LandingHeader />

      {/* ─── HERO ─── */}
      <section className="relative min-h-[620px] lg:min-h-[700px] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background image */}
        <img
          src={IMAGES.hero}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Gradient overlay — dark left for text, lighter right for image */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#161B26] via-[rgba(10,11,15,0.75)] to-[rgba(10,11,15,0.30)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full grid lg:grid-cols-2 gap-12 items-center py-24">
          {/* Left — copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-base text-[#A0AEC0] mb-3 font-medium">Never Miss a Lead. Never Chase Admin.</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.12] mb-5">
              Leads, jobs, and follow-ups<br />
              <span className="text-[#14F195]">handled automatically.</span>
            </h1>
            <p className="text-lg text-[#A0AEC0] mb-8 max-w-md leading-relaxed">
              Capture more jobs, respond instantly, and eliminate hours of admin.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate('/login')}
                data-testid="hero-cta-primary"
                className="group px-7 py-3.5 bg-[#14F195] hover:bg-[#11D882] text-[#161B26] rounded-xl font-semibold text-sm transition-colors flex items-center gap-2"
              >
                Get My AI Plan
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta-secondary"
                className="px-7 py-3.5 rounded-xl font-semibold text-sm bg-[rgba(255,255,255,0.06)] border border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.1)] transition-all text-white"
              >
                See It In Action
              </button>
            </div>
          </motion.div>

          {/* Right — floating activity dashboard */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="hidden lg:flex justify-end"
            data-testid="hero-dashboard"
          >
            <div className={`${PANEL} p-5 w-[380px] backdrop-blur-sm bg-[rgba(15,16,23,0.85)]`}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#14F195]" />
                <span className="text-xs text-[#9CA3B8] font-medium tracking-wide uppercase">Live</span>
                <span className="text-[10px] text-[#7B8299] ml-auto">Today</span>
              </div>
              <div className="grid grid-cols-3 gap-2.5 mb-2.5">
                {[
                  { v: '12', l: 'Leads', c: '#14F195' },
                  { v: '8', l: 'Jobs', c: '#3B82F6' },
                  { v: '24', l: 'Tasks', c: '#A855F7' },
                ].map((m, i) => (
                  <div key={i} className="rounded-lg p-3 border" style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: `${m.c}15`, boxShadow: `0 0 16px ${m.c}12` }}>
                    <p className="text-xl font-bold" style={{ color: m.c }}>{m.v}</p>
                    <p className="text-[9px] text-[#9CA3B8]">{m.l}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2.5 mb-4">
                {[
                  { v: '18 hrs', l: 'Saved', c: '#6366F1' },
                  { v: '$3,200', l: 'Revenue', c: '#EC4899' },
                ].map((m, i) => (
                  <div key={i} className="rounded-lg p-3 border" style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: `${m.c}15`, boxShadow: `0 0 16px ${m.c}12` }}>
                    <p className="text-xl font-bold" style={{ color: m.c }}>{m.v}</p>
                    <p className="text-[9px] text-[#9CA3B8]">{m.l}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-[rgba(255,255,255,0.04)] pt-3">
                <p className="text-[9px] text-[#7B8299] uppercase tracking-wider font-medium mb-2.5">Activity</p>
                <div className="space-y-2">
                  {[
                    { t: 'New lead captured', c: '#14F195', d: '2m' },
                    { t: 'Job booked automatically', c: '#3B82F6', d: '5m' },
                    { t: 'Quote sent', c: '#A855F7', d: '12m' },
                    { t: 'Follow-up triggered', c: '#EC4899', d: '18m' },
                  ].map((a, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: a.c, boxShadow: `0 0 6px ${a.c}` }} />
                        <span className="text-[11px] text-[#8a90a8]">{a.t}</span>
                      </div>
                      <span className="text-[9px] text-[#7B8299]">{a.d}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── WHAT THIS LOOKS LIKE ─── */}
      <section className="py-24 bg-[#1A1F2B]" data-testid="outcomes-section">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What Changes in <span className="text-[#14F195]">Your Business</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Phone, title: 'Never miss another enquiry', desc: 'Every lead answered instantly. No missed calls.', color: '#14F195' },
              { icon: CalendarCheck, title: 'Jobs booked without back-and-forth', desc: 'Customers confirm directly. No chasing.', color: '#3B82F6' },
              { icon: PaperPlaneTilt, title: 'Follow-ups happen automatically', desc: "Quotes don't go cold. Nothing slips through.", color: '#A855F7' },
              { icon: GearSix, title: 'Admin handled in the background', desc: 'Less paperwork. More billable work.', color: '#6366F1' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  data-testid={`outcome-card-${i}`}
                  className="p-6 rounded-xl border border-[rgba(255,255,255,0.10)] bg-[#1E2836] shadow-[0_2px_12px_rgba(0,0,0,0.25)]"
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${item.color}12` }}>
                    <Icon size={20} style={{ color: item.color }} />
                  </div>
                  <h3 className="text-white font-semibold text-sm mb-2">{item.title}</h3>
                  <p className="text-[#A0AEC0] text-xs leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── YOUR BUSINESS, RUN DIFFERENTLY ─── */}
      <section className="py-24" data-testid="vision-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight">
                This Is What It Looks Like <span className="text-[#14F195]">When It's Working</span>
              </h2>
              <p className="text-base text-[#A0AEC0] leading-relaxed mb-3">
                Enquiries are handled instantly. Jobs get booked without chasing.
              </p>
              <p className="text-base text-[#A0AEC0] leading-relaxed">
                Your team focuses on the work, not admin.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="flex justify-center">
              <img
                src={IMAGES.team}
                alt="Service business team reviewing operations"
                className="rounded-2xl w-full max-w-lg object-cover"
                loading="lazy"
                data-testid="vision-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section id="how-it-works" className="py-24 bg-[#1A1F2B]" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Set Up in Minutes. <span className="text-[#14F195]">Results From Day One.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', icon: Lightbulb, title: 'We map how your business runs today', desc: '' },
              { step: '02', icon: Cpu, title: 'We set up systems to handle enquiries, bookings, and follow-ups', desc: '' },
              { step: '03', icon: Lightning, title: 'Everything runs automatically. No extra work for your team.', desc: '' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="relative"
                  data-testid={`how-it-works-step-${item.step}`}
                >
                  <div className="p-8 rounded-2xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)] h-full">
                    <span className="text-5xl font-bold text-[rgba(20,241,149,0.1)] absolute top-4 right-4">{item.step}</span>
                    <div className="w-12 h-12 rounded-xl bg-[rgba(20,241,149,0.08)] flex items-center justify-center mb-5">
                      <Icon size={24} className="text-[#14F195]" />
                    </div>
                    <h3 className="text-base font-semibold text-white mb-2">{item.title}</h3>
                    {item.desc && <p className="text-[#A0AEC0] text-sm">{item.desc}</p>}
                  </div>
                  {i < 2 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 -translate-y-1/2 z-10">
                      <ArrowRight size={18} className="text-[#2D3348]" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── AI PLAN ─── */}
      <section className="py-24" data-testid="ai-plan-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">
                Your Plan, Built for <span className="text-[#14F195]">Your Business</span>
              </h2>
              <p className="text-base text-[#A0AEC0] mb-6 leading-relaxed">
                We show exactly where you're losing leads and time. Then fix it.
              </p>
              <div className="space-y-3">
                {[
                  'Shows exactly where you\'re losing leads and time',
                  'Recommends systems that capture and convert more jobs',
                  'Estimates time and revenue impact before you start',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <CheckCircle size={18} className="text-[#14F195] flex-shrink-0" />
                    <span className="text-sm text-[#A0AEC0]">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="flex justify-center" data-testid="ai-plan-visual">
              <div className={`${PANEL} p-6 max-w-sm w-full`}>
                <div className="flex items-center gap-2 mb-5">
                  <Lightbulb size={14} className="text-[#14F195]" />
                  <span className="text-xs text-[#9CA3B8] font-medium tracking-wide uppercase">Your AI Plan</span>
                </div>
                <div className="rounded-xl p-4 border mb-4" style={{ backgroundColor: 'rgba(20,241,149,0.04)', borderColor: 'rgba(20,241,149,0.12)', boxShadow: '0 0 20px rgba(20,241,149,0.08)' }}>
                  <p className="text-[10px] text-[#9CA3B8] mb-0.5">Opportunities Found</p>
                  <p className="text-3xl font-bold text-[#14F195]">3</p>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div className="rounded-xl p-3 border" style={{ backgroundColor: 'rgba(99,102,241,0.04)', borderColor: 'rgba(99,102,241,0.12)' }}>
                    <p className="text-lg font-bold text-[#6366F1]">22 hrs</p>
                    <p className="text-[10px] text-[#9CA3B8]">Saved per week</p>
                  </div>
                  <div className="rounded-xl p-3 border" style={{ backgroundColor: 'rgba(236,72,153,0.04)', borderColor: 'rgba(236,72,153,0.12)' }}>
                    <p className="text-lg font-bold text-[#EC4899]">$4,800</p>
                    <p className="text-[10px] text-[#9CA3B8]">Monthly impact</p>
                  </div>
                </div>
                <div className="border-t border-[rgba(255,255,255,0.04)] pt-4">
                  <p className="text-[10px] text-[#7B8299] uppercase tracking-wider font-medium mb-3">Recommended Systems</p>
                  <div className="space-y-2">
                    {['Lead Capture', 'Job Scheduling', 'Follow-up Automation'].map((s, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle size={14} className="text-[#14F195]" />
                        <span className="text-xs text-[#8a90a8]">{s}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── INTEGRATIONS CAROUSEL ─── */}
      <section className="py-20 bg-[#1A1F2B] overflow-hidden" data-testid="integrations-section">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Works With Your Tools
            </h2>
            <p className="text-sm text-[#A0AEC0] mb-3">
              Works with the systems you already use. No disruption required.
            </p>
            <p className="text-xs text-[#4a4f6a] mb-10">
              Set up in minutes, not months.
            </p>
          </motion.div>

          {/* Infinite auto-scroll carousel */}
          <div className="relative overflow-hidden">
            <div className="flex gap-6 animate-scroll-left" style={{ width: 'max-content' }}>
              {/* Two copies for seamless infinite loop */}
              {[0, 1].map((copy) => (
                <div key={copy} className="flex gap-6 shrink-0">
                  {[
                    { name: 'Gmail', slug: 'gmail' },
                    { name: 'Google Calendar', slug: 'googlecalendar' },
                    { name: 'Slack', slug: 'slack' },
                    { name: 'HubSpot', slug: 'hubspot' },
                    { name: 'Salesforce', slug: 'salesforce' },
                    { name: 'QuickBooks', slug: 'quickbooks_sandbox' },
                    { name: 'Xero', slug: 'xero' },
                    { name: 'Calendly', slug: 'calendly' },
                    { name: 'Zendesk', slug: 'zendesk' },
                    { name: 'Mailchimp', slug: 'mailchimp' },
                    { name: 'Google Sheets', slug: 'googlesheets' },
                    { name: 'Pipedrive', slug: 'pipedrive' },
                    { name: 'Notion', slug: 'notion' },
                    { name: 'Airtable', slug: 'airtable' },
                    { name: 'Google Drive', slug: 'googledrive' },
                    { name: 'Outlook', slug: 'outlook' },
                    { name: 'Monday.com', slug: 'monday' },
                    { name: 'Asana', slug: 'asana' },
                    { name: 'Freshbooks', slug: 'freshbooks' },
                    { name: 'Trello', slug: 'trello' },
                  ].map((app, i) => (
                    <div key={`${copy}-${i}`}
                      className="flex items-center gap-3 px-5 py-3 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.15)] transition-all duration-300 shrink-0 group"
                      data-testid={`carousel-app-${app.slug}`}
                    >
                      <img
                        src={`https://logos.composio.dev/api/${app.slug}`}
                        alt={app.name}
                        className="w-6 h-6 rounded object-contain group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => { e.target.style.display = 'none'; }}
                        loading="lazy"
                      />
                      <span className="text-xs text-[#8a90a8] font-medium whitespace-nowrap group-hover:text-white transition-colors duration-300">{app.name}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── TRUST STRIP ─── */}
      <section className="py-16" data-testid="trust-section">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-sm text-[#A0AEC0] mb-8">
              Built for service businesses like plumbing, electrical, HVAC, and cleaning.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              {[
                { label: 'Respond faster', color: '#14F195' },
                { label: 'Book more jobs', color: '#3B82F6' },
                { label: 'Reduce admin time', color: '#A855F7' },
              ].map((m, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.color }} />
                  <span className="text-sm text-white font-medium">{m.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── SECTION BREAK ─── */}
      <section className="relative h-16 overflow-hidden" data-testid="section-break">
        <img src={IMAGES.techDetail} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-center" style={{ opacity: 0.1 }} loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#161B26] via-transparent to-[#1A1F2B]" />
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-28 bg-[#1A1F2B]" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">
              Stop Losing Leads. <span className="text-[#14F195]">Start Running a Tighter Business.</span>
            </h2>
            <p className="text-base text-[#A0AEC0] mb-10">
              See exactly what to automate and how much time and revenue you'll recover.
            </p>
            <button
              onClick={() => navigate('/login')}
              data-testid="final-cta-button"
              className="group px-8 py-4 bg-[#14F195] hover:bg-[#11D882] text-[#161B26] rounded-xl font-semibold text-base transition-colors inline-flex items-center gap-2"
            >
              Get My AI Plan
              <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewHome;
